import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent, Checkbox, Container, Dialog, DialogContent, DialogTitle, FormControl, FormControlLabel, FormLabel, IconButton, LinearProgress, Pagination, Popover, Radio, RadioGroup, Snackbar, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { connectSocket, disconnectSocket, socket } from "../socket";
import { validateToken } from "../utils/Cookie";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import VideocamIcon from '@mui/icons-material/Videocam';
import HighQualityOutlinedIcon from '@mui/icons-material/HighQualityOutlined';
import CenterFocusStrongOutlinedIcon from '@mui/icons-material/CenterFocusStrongOutlined';
import LandscapeOutlinedIcon from '@mui/icons-material/LandscapeOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import PhotoSizeSelectSmallOutlinedIcon from '@mui/icons-material/PhotoSizeSelectSmallOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

type FotoProduto = {
  id: number;
  codigo: string;
  empresa_id: number;
  imagem_id: number | null;
  arquivo_original: string | null;
  capa_url: string | null;
  total_imagens: number;
  total_videos: number;
  media_counts: Record<DownloadMediaType, number>;
  nome: string;
};

type MediaAsset = {
  id: string;
  source: DownloadMediaType;
  fileName: string;
  previewUrl: string;
  relatedSkus?: string[];
  system?: string;
};

type DownloadMediaType = 'alta' | 'still' | 'ambientadas' | 'redes_sociais' | 'thumbs' | 'embalagens' | 'videos';
type ZipOrganization = 'by_sku' | 'by_type' | 'flat';

const MEDIA_OPTIONS: Array<{ value: DownloadMediaType; label: string; shortLabel: string }> = [
  { value: 'alta', label: 'Fotos originais em alta', shortLabel: 'Alta' },
  { value: 'still', label: 'Fotos still processadas', shortLabel: 'Still' },
  { value: 'ambientadas', label: 'Fotos ambientadas', shortLabel: 'Ambientadas' },
  { value: 'redes_sociais', label: 'Redes sociais', shortLabel: 'Redes sociais' },
  { value: 'thumbs', label: 'Thumbnails', shortLabel: 'Thumbnails' },
  { value: 'embalagens', label: 'Fotos das embalagens', shortLabel: 'Embalagens' },
  { value: 'videos', label: 'Vídeos', shortLabel: 'Vídeos' },
];

const getMediaIcon = (type: DownloadMediaType, fontSize: number | string = 18) => {
  const props = { sx: { fontSize } };
  if (type === 'alta') return <HighQualityOutlinedIcon {...props} />;
  if (type === 'still') return <CenterFocusStrongOutlinedIcon {...props} />;
  if (type === 'ambientadas') return <LandscapeOutlinedIcon {...props} />;
  if (type === 'redes_sociais') return <ShareOutlinedIcon {...props} />;
  if (type === 'thumbs') return <PhotoSizeSelectSmallOutlinedIcon {...props} />;
  if (type === 'embalagens') return <Inventory2OutlinedIcon {...props} />;
  return <VideocamIcon {...props} />;
};

const DOWNLOAD_INACTIVITY_TIMEOUT_MS = 3 * 60 * 1000;
const SEARCH_INACTIVITY_TIMEOUT_MS = 2 * 60 * 1000;
const ITEMS_PER_PAGE = 30;

function BouncingDots() {
  return (
    <Box
      aria-label="Carregando"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 1,
        height: 36,
        pt: 1.5,
        mt: 0,
        mb: 1,
        overflow: 'visible',
        '& span': {
          width: 12,
          height: 12,
          bgcolor: '#1A3661',
          borderRadius: '50%',
          animation: 'media-loading-bounce 1.5s infinite ease-in-out'
        },
        '& span:nth-of-type(2)': { animationDelay: '0.2s' },
        '& span:nth-of-type(3)': { animationDelay: '0.4s' },
        '@keyframes media-loading-bounce': {
          '0%, 80%, 100%': { transform: 'translateY(0)' },
          '40%': { transform: 'translateY(-12px)' }
        }
      }}
    >
      <span />
      <span />
      <span />
    </Box>
  );
}

export default function GeracaoFotos() {
  const azureId = localStorage.getItem("azureId") || '';
  const navigate = useNavigate();
  const codProsOrderRef = useRef<string[]>([]);
  const lastSearchedCodesRef = useRef<string[]>([]);
  const requestedMediaTypesRef = useRef<DownloadMediaType[]>(['alta', 'ambientadas']);
  const skipDescriptionsRef = useRef(false);
  const dataFotosRef = useRef<FotoProduto[]>([]);
  const downloadTimeoutRef = useRef<number | null>(null);
  const searchTimeoutRef = useRef<number | null>(null);
  const resultsGridRef = useRef<HTMLDivElement | null>(null);
  const [codeProduct, setCodeProduct] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchProgress, setSearchProgress] = useState({ completed: 0, total: 0, currentSku: '', mediaFound: 0 });
  const [dataFotos, setDataFotos] = useState<FotoProduto[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<FotoProduto | null>(null);
  const [modalFotos, setModalFotos] = useState<Record<string, MediaAsset[]>>({});
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const fotosDoProduto = modalFotos[selectedProduct?.codigo || ''] || modalFotos[String(selectedProduct?.codigo).trim().toUpperCase()] || [];
  const [isDownloadingFotos, setIsDownloadingFotos] = useState(false);
  const [isDownloadingVideos, setIsDownloadingVideos] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ completed: 0, total: 0, currentMedia: '' });
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [typeMessage, setTypeMessage] = useState<'info' | 'error' | 'warning'>('info');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [modalVideos, setModalVideos] = useState<Record<string, MediaAsset[]>>({});
  const videosDoProduto = modalVideos[selectedProduct?.codigo || ''] || modalVideos[String(selectedProduct?.codigo).trim().toUpperCase()] || [];
  const [previewVideo, setPreviewVideo] = useState<string | null>(null);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [activeMediaTypes, setActiveMediaTypes] = useState<DownloadMediaType[]>(['alta', 'ambientadas']);
  const [draftMediaTypes, setDraftMediaTypes] = useState<DownloadMediaType[]>(['alta', 'ambientadas']);
  const [loadedMediaTypes, setLoadedMediaTypes] = useState<DownloadMediaType[]>([]);
  const [zipOrganization, setZipOrganization] = useState<ZipOrganization>('by_sku');
  const [currentPage, setCurrentPage] = useState(1);
  const filteredDataFotos = dataFotos.filter(item =>
    activeMediaTypes.some(mediaType => Number(item.media_counts?.[mediaType] || 0) > 0)
  );
  const totalPages = Math.ceil(filteredDataFotos.length / ITEMS_PER_PAGE);
  const currentPageItems = filteredDataFotos.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  const visibleModalMediaTypes = activeMediaTypes.filter(mediaType =>
    mediaType === 'videos'
      ? videosDoProduto.length > 0
      : fotosDoProduto.some(media => media.source === mediaType)
  );
  const filtersDirty = draftMediaTypes.length !== activeMediaTypes.length ||
    draftMediaTypes.some(mediaType => !activeMediaTypes.includes(mediaType));
  const cardMinWidth = Math.min(300, 260 + activeMediaTypes.length * 8);
  const informedCodesCount = new Set(codeProduct.split(/[\s,]+/).filter(Boolean)).size;

  const handleOpenImagePreview = (event: React.SyntheticEvent<HTMLElement>, img: string) => {
    setAnchorEl(event.currentTarget);
    setPreviewVideo(null);
    setPreviewImage(img);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
    setPreviewImage(null);
  };

  const handleMouseEnterVideo = (event: React.MouseEvent<HTMLElement>, video: string) => {
    setAnchorEl(event.currentTarget);
    setPreviewImage(null);
    setPreviewVideo(video);
  };

  const handleMouseLeaveVideo = () => {
    setAnchorEl(null);
    setPreviewVideo(null);
  };

  const resetDownloadProgress = () => {
    setDownloadProgress({ completed: 0, total: 0, currentMedia: '' });
  };

  const clearDownloadTimeout = () => {
    if (downloadTimeoutRef.current !== null) {
      window.clearTimeout(downloadTimeoutRef.current);
      downloadTimeoutRef.current = null;
    }
  };

  const resetDownloadTimeout = () => {
    clearDownloadTimeout();
    downloadTimeoutRef.current = window.setTimeout(() => {
      setIsDownloadingFotos(false);
      setIsDownloadingVideos(false);
      setSnackbarMessage('O download ficou sem atualização por mais de 3 minutos. Tente novamente.');
      setTypeMessage('error');
      setOpenSnackbar(true);
    }, DOWNLOAD_INACTIVITY_TIMEOUT_MS);
  };

  const finishDownload = () => {
    clearDownloadTimeout();
    setIsDownloadingFotos(false);
    setIsDownloadingVideos(false);
  };

  const clearSearchTimeout = () => {
    if (searchTimeoutRef.current !== null) {
      window.clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  };

  const resetSearchTimeout = () => {
    clearSearchTimeout();
    searchTimeoutRef.current = window.setTimeout(() => {
      setIsLoading(false);
      setSnackbarMessage('A busca ficou sem atualização por mais de dois minutos. Tente novamente.');
      setTypeMessage('error');
      setOpenSnackbar(true);
    }, SEARCH_INACTIVITY_TIMEOUT_MS);
  };

  useEffect(() => {
    return () => {
      if (downloadTimeoutRef.current !== null) {
        window.clearTimeout(downloadTimeoutRef.current);
      }
      if (searchTimeoutRef.current !== null) {
        window.clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSelectAll = () => {
    const filteredCodes = filteredDataFotos.map(item => String(item.codigo).trim().toUpperCase());
    if (filteredCodes.length > 0 && filteredCodes.every(code => selectedProducts.includes(code))) {
      setSelectedProducts([]); // desmarca tudo
    } else {
      setSelectedProducts(filteredCodes);
    }
  };

  const handleClearSelection = () => {
    setSelectedProducts([]);
  };

  const handleOpenDownloadOptions = () => {
    setZipOrganization('by_sku');
    setOpenDownloadModal(true);
  };

  const handleMediaFilterChange = (_event: React.MouseEvent<HTMLElement>, values: DownloadMediaType[]) => {
    if (values.length === 0) return;
    setDraftMediaTypes(values);
  };

  const handleToggleAllMedia = () => {
    const values: DownloadMediaType[] = draftMediaTypes.length === MEDIA_OPTIONS.length
      ? ['alta', 'ambientadas']
      : MEDIA_OPTIONS.map(option => option.value);
    setDraftMediaTypes(values);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    window.requestAnimationFrame(() => {
      resultsGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const toggleSelectProduct = (codigo: string) => {
    const codeStr = String(codigo).trim().toUpperCase();
    setSelectedProducts(prev =>
      prev.includes(codeStr)
        ? prev.filter(c => c !== codeStr)
        : [...prev, codeStr]
    );
  };

  const handleOpenModal = (item: FotoProduto) => {
    setSelectedProduct(item);
    setOpenModal(true);
    setIsLoadingModal(false);
  };

  const handleBuscarFotos = () => {
    //máximo de produtos por busca é 150
    const uniqueCodes = [...new Set(codeProduct.split(/[\s,]+/).filter((item) => item !== ''))];
    if (uniqueCodes.length > 150) {
      setSnackbarMessage("Limite de 150 códigos por busca. Remova os códigos extras e tente novamente.");
      setTypeMessage('warning');
      setOpenSnackbar(true);
      return;
    }

    // A consulta precisa retornar todos os códigos pesquisados para que a
    // validação de "não encontrados" não confunda itens paginados com ausentes.
    const codPros = uniqueCodes;
    if (codPros.length === 0) {
      setSnackbarMessage('Informe pelo menos um código de produto.');
      setTypeMessage('warning');
      setOpenSnackbar(true);
      return;
    }

    const normalizedCodes = codPros.map(code => code.trim().toUpperCase());
    const sameProducts = normalizedCodes.length === lastSearchedCodesRef.current.length &&
      normalizedCodes.every((code, index) => code === lastSearchedCodesRef.current[index]);
    const filtersAlreadyLoaded = draftMediaTypes.every(mediaType => loadedMediaTypes.includes(mediaType));

    if (sameProducts && dataFotosRef.current.length > 0 && filtersAlreadyLoaded) {
      setActiveMediaTypes(draftMediaTypes);
      setCurrentPage(1);
      const matchingCodes = new Set(
        dataFotosRef.current
          .filter(item => draftMediaTypes.some(mediaType => Number(item.media_counts?.[mediaType] || 0) > 0))
          .map(item => String(item.codigo).trim().toUpperCase())
      );
      setSelectedProducts(current => current.filter(code => matchingCodes.has(code)));
      return;
    }

    codProsOrderRef.current = codPros;
    requestedMediaTypesRef.current = [...draftMediaTypes];
    skipDescriptionsRef.current = sameProducts && dataFotosRef.current.length > 0;

    if (!sameProducts) setSelectedProducts([]);
    setCurrentPage(1);

    setIsLoading(true);
    setSearchProgress({ completed: 0, total: codPros.length, currentSku: '', mediaFound: 0 });
    resetSearchTimeout();
    setOpenModal(false);
    setSelectedProduct(null);
    if (!sameProducts) {
      setModalFotos({});
      setModalVideos({});
    }

    socket.emit("busca_fotos", {
      azureId,
      codEmp: Number(localStorage.getItem("empresa")),
      codPros,
      mediaTypes: draftMediaTypes,
      skipDescriptions: skipDescriptionsRef.current,
      page: 1,
      total: rowCount,
      limit: codPros.length
    });

  };

  useEffect(() => {
    if (!validateToken()) navigate('/login?logout=true');

    connectSocket();

    const eventName = `finish_busca_fotos_${azureId}`;
    const progressEventName = `busca_fotos_progress_${azureId}`;

    const progressHandler = (progress: { completed?: number; total?: number; currentSku?: string; mediaFound?: number }) => {
      resetSearchTimeout();
      setSearchProgress({
        completed: progress.completed || 0,
        total: progress.total || 0,
        currentSku: progress.currentSku || '',
        mediaFound: progress.mediaFound || 0,
      });
    };

    const handler = (response: any) => {
      clearSearchTimeout();
      setIsLoading(false);

      if (response?.status !== 200) {
        if (!skipDescriptionsRef.current) {
          setDataFotos([]);
          dataFotosRef.current = [];
        }
        setSnackbarMessage(response?.message || 'Não foi possível consultar as mídias no Azure.');
        setTypeMessage('error');
        setOpenSnackbar(true);
        return;
      }

      const existingNames = new Map(
        dataFotosRef.current.map(item => [String(item.codigo).trim().toUpperCase(), item.nome])
      );
      const rows = (response?.data || []).map((item: any) => {
        const sku = String(item.codigo).trim().toUpperCase();
        return {
          ...item,
          nome: skipDescriptionsRef.current ? existingNames.get(sku) || item.nome : item.nome,
          total_imagens: Number(item.total_imagens),
          total_videos: Number(item.total_videos)
        };
      });
      setModalFotos(response?.fotos || {});
      setModalVideos(response?.videos || {});

      const searchedCodes = [...new Set(codProsOrderRef.current.map(c => c.toUpperCase().trim()).filter(c => c))];
      const foundCodes = new Set(rows.map((item: FotoProduto) => String(item.codigo).toUpperCase().trim()));
      const notFoundCodes = searchedCodes.filter(code => !foundCodes.has(code));

      if (notFoundCodes.length > 0) {
        setSnackbarMessage(
          `Os códigos a seguir não possuem mídias nos filtros selecionados: ${notFoundCodes.join(', ')}`
        );
        setTypeMessage('warning');
        setOpenSnackbar(true);
      }

      // remove duplicados mantendo ordem original
      const uniqueOrder = Array.from(
        new Set(codProsOrderRef.current)
      );

      const orderMap = new Map(
        uniqueOrder.map((cod, index) => [cod, index])
      );

      const sorted = [...rows].sort((a, b) => {
        return (orderMap.get(String(a.codigo)) ?? 9999) -
          (orderMap.get(String(b.codigo)) ?? 9999);
      });

      setDataFotos(sorted);
      dataFotosRef.current = sorted;
      setActiveMediaTypes(requestedMediaTypesRef.current);
      setLoadedMediaTypes(requestedMediaTypesRef.current);
      lastSearchedCodesRef.current = codProsOrderRef.current.map(code => code.trim().toUpperCase());
      setSelectedProducts(current => current.filter(code =>
        sorted.some(item => String(item.codigo).trim().toUpperCase() === code)
      ));

      if (sorted.length > 0) {
        setRowCount(sorted[0].total_imagens);
      }
    };

    socket.on(eventName, handler);
    socket.on(progressEventName, progressHandler);

    return () => {
      socket.off(eventName, handler);
      socket.off(progressEventName, progressHandler);
      disconnectSocket();
    };
  }, [navigate, azureId]);

  useEffect(() => {
    const event = `finish_zip_fotos_${azureId}`;

    const handler = (response: any) => {
      finishDownload();

      if (response?.success && response.url) {
        const link = document.createElement("a");
        link.href = response.url;
        link.click();
      } else {
        setSnackbarMessage('Não foi possível preparar o download. Tente novamente.');
        setTypeMessage('error');
        setOpenSnackbar(true);
      }
    };

    socket.on(event, handler);

    return () => {
      socket.off(event, handler);
    };
  }, [azureId]);

  useEffect(() => {
    const event = `zip_progress_${azureId}`;

    const handler = (progress: { completed?: number; total?: number; currentMedia?: string }) => {
      resetDownloadTimeout();
      setDownloadProgress({
        completed: progress.completed || 0,
        total: progress.total || 0,
        currentMedia: progress.currentMedia || ''
      });
    };

    socket.on(event, handler);

    return () => {
      socket.off(event, handler);
    };
  }, [azureId]);

  useEffect(() => {
    const event = `finish_zip_videos_${azureId}`;

    const handler = (response: any) => {
      finishDownload();

      if (response?.success && response.url) {
        const link = document.createElement("a");
        link.href = response.url;
        link.click();
      } else {
        setSnackbarMessage('Não foi possível preparar o download de vídeos. Tente novamente.');
        setTypeMessage('error');
        setOpenSnackbar(true);
      }
    };

    socket.on(event, handler);

    return () => {
      socket.off(event, handler);
    };
  }, [azureId]);

  const handleDownloadAll = () => {
    if (!selectedProduct) return;

    resetDownloadProgress();
    resetDownloadTimeout();
    setIsDownloadingFotos(true);

    socket.emit("gerar_zip_midias_lote", {
      azureId,
      codPros: [selectedProduct.codigo],
      codEmp: Number(localStorage.getItem("empresa")),
      mediaTypes: activeMediaTypes,
      zipOrganization: 'by_sku'
    });
  };

  const handleDownloadCategory = (mediaType: DownloadMediaType) => {
    const hasMedia = mediaType === 'videos'
      ? videosDoProduto.length > 0
      : fotosDoProduto.some(media => media.source === mediaType);
    if (!hasMedia) return;

    resetDownloadProgress();
    resetDownloadTimeout();
    setIsDownloadingFotos(mediaType !== 'videos');
    setIsDownloadingVideos(mediaType === 'videos');

    socket.emit("gerar_zip_midias_lote", {
      azureId,
      codPros: [selectedProduct?.codigo || ''],
      codEmp: Number(localStorage.getItem("empresa")),
      mediaTypes: [mediaType],
      zipOrganization: 'by_sku'
    });
  };

  useEffect(() => {
    const event = `finish_zip_midias_${azureId}`;

    const handler = (response: any) => {
      finishDownload();

      if (response?.success && response.url) {
        const link = document.createElement("a");
        link.href = response.url;
        link.click();
        if (response.partial) {
          setSnackbarMessage(
            `Download concluído parcialmente: ${response.included} arquivo(s) incluído(s) e ${response.failed} com falha.`
          );
          setTypeMessage('warning');
          setOpenSnackbar(true);
        }
      } else {
        setSnackbarMessage(response?.message || 'Não foi possível preparar o download das mídias. Tente novamente.');
        setTypeMessage('error');
        setOpenSnackbar(true);
      }
    };

    socket.on(event, handler);

    return () => {
      socket.off(event, handler);
    };
  }, [azureId]);

  const handleDownloadMultiple = () => {
    if (!selectedProducts.length || !activeMediaTypes.length) return;

    setOpenDownloadModal(false);
    resetDownloadProgress();
    resetDownloadTimeout();
    setIsDownloadingFotos(true);

    const codProsOriginal = selectedProducts.map(c => {
      const codeOriginal = dataFotos.find(d => String(d.codigo).trim().toUpperCase() === c)?.codigo || c;
      return String(codeOriginal);
    });

    socket.emit("gerar_zip_midias_lote", {
      azureId,
      codPros: codProsOriginal,
      codEmp: Number(localStorage.getItem("empresa")),
      mediaTypes: activeMediaTypes,
      zipOrganization
    });
  };

  const handleDownloadSingleMedia = (media: MediaAsset) => {
    const backend = process.env.REACT_APP_BACKEND

    window.open(
      `${backend}/download-image?blobName=${encodeURIComponent(media.id)}`,
      '_blank'
    )
  }

  const ProdutoCard = ({ item }: { item: FotoProduto }) => {
    const itemCodeStr = String(item.codigo).trim().toUpperCase();
    const isSelected = selectedProducts.includes(itemCodeStr);

    const imageUrl = item.capa_url || '';

    return (
      <Card
        onClick={() => handleOpenModal(item)}
        sx={{
          width: '100%',
          maxWidth: 300,
          height: '100%',
          minHeight: activeMediaTypes.length <= 2 ? 280 : 295,
          minWidth: 0,
          borderRadius: 2,
          boxShadow: 3,
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          transition: '0.2s',
          position: 'relative',
          border: isSelected ? '2px solid #1a36615e' : '2px solid transparent',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6
          }
        }}
      >
        {/* CHECKBOX */}
        <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
          <Checkbox
            checked={isSelected}
            onClick={(e) => {
              e.stopPropagation();
              toggleSelectProduct(item.codigo);
            }}
          />
        </div>

        <div
          style={{
            position: 'relative',
            height: 160,
            display: 'flex',
            alignItems: 'flex-end',
            padding: 10,
            overflow: 'hidden',
            background: imageUrl ? '#f7f7f7' : 'linear-gradient(135deg, #4e89c7, #1a3661)'
          }}
        >
          {imageUrl && (
            <img
              src={imageUrl}
              alt={`Produto ${item.codigo}`}
              loading="lazy"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          )}
          {/* overlay */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.28), transparent 45%)'
            }}
          />

          <Typography
            sx={{
              position: 'relative',
              backgroundColor: 'rgba(26,54,97,0.76)',
              color: '#fff',
              fontSize: 12,
              fontWeight: 600,
              padding: '4px 8px',
              borderRadius: 1,
              boxShadow: '0 1px 3px rgba(0,0,0,0.18)'
            }}
          >
            {item.codigo}
          </Typography>
        </div>

        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: activeMediaTypes.length <= 2 ? 112 : 126,
            p: 1.75,
            '&:last-child': { pb: 1.75 }
          }}
        >
          <Typography fontWeight={600} fontSize={14}>
            {item.nome}
          </Typography>

          <div
            style={{
              display: 'flex',
              gap: 4,
              flexWrap: 'nowrap',
              marginTop: 'auto',
              width: '100%',
              justifyContent: activeMediaTypes.length >= 4 ? 'center' : 'flex-start'
            }}
          >
            {activeMediaTypes.map(mediaType => {
              const option = MEDIA_OPTIONS.find(item => item.value === mediaType)!;
              const count = Number(item.media_counts?.[mediaType] || 0);
              return (
                <Tooltip key={mediaType} title={`${option.label}: ${count}`} arrow>
                  <Box
                    aria-label={`${option.label}: ${count}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: count > 0 ? '#f3f6fa' : '#f5f5f5',
                      color: count > 0 ? '#1A3661' : 'text.disabled',
                      px: 0.6,
                      py: 0.5,
                      mt: 2,
                      borderRadius: 1.5,
                    }}
                  >
                    {getMediaIcon(mediaType, 15)}
                    <Typography fontSize={11}>{count}</Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="xl">
      <Card sx={{ minWidth: 275, backgroundColor: '#fff', marginBottom: '2%' }}>
        <CardContent className="cardContent">
          <Box sx={{ bgcolor: '#1A3661' }}><Typography gutterBottom className="cardTitle">Geração de Dados</Typography></Box>
          <div className="cardInfo">
            <TextField
              label="Digite o Código do Produto"
              variant="outlined"
              sx={{ width: '100% !important', marginRight: '8px' }}
              value={codeProduct}
              onChange={e => setCodeProduct(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  handleBuscarFotos();
                }
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </div>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mx: 2, mt: 2, p: 1.5, border: '1px solid #e3e8ef', borderRadius: 2, bgcolor: '#fff' }}>
            <Typography fontSize={13} fontWeight={600} color="#1A3661" sx={{ mr: 0.5 }}>Filtrar por</Typography>
            <ToggleButton value="all" selected={draftMediaTypes.length === MEDIA_OPTIONS.length} onClick={handleToggleAllMedia} size="small" sx={{ textTransform: 'none', borderRadius: '6px !important', px: 1.5, '&.Mui-selected, &.Mui-selected:hover': { bgcolor: '#1A3661', color: '#fff' } }}>
              Todos
            </ToggleButton>
            <ToggleButtonGroup value={draftMediaTypes} onChange={handleMediaFilterChange} size="small" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, '& .MuiToggleButtonGroup-grouped': { borderRadius: '6px !important', border: '1px solid rgba(0,0,0,0.12) !important', '&.Mui-selected, &.Mui-selected:hover': { bgcolor: '#1A3661', color: '#fff' } } }}>
              {MEDIA_OPTIONS.map(option => (
                <Tooltip key={option.value} title={option.label} arrow>
                  <ToggleButton value={option.value} aria-label={option.label} sx={{ textTransform: 'none', gap: 0.75, px: 1.25 }}>
                    {getMediaIcon(option.value, 16)}
                    {option.shortLabel}
                  </ToggleButton>
                </Tooltip>
              ))}
            </ToggleButtonGroup>
            {filtersDirty && dataFotos.length > 0 && <Typography variant="caption" sx={{ ml: 'auto', color: '#b26a00', fontWeight: 600 }}>Filtros alterados — aplique para atualizar.</Typography>}
          </Box>
          <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, px: 2, pt: 2, pb: 2 }}>
            <Typography variant="caption" sx={{ color: '#667085' }}>{informedCodesCount} {informedCodesCount === 1 ? 'código informado' : 'códigos informados'} • limite de 150</Typography>
            <Button sx={{ textTransform: 'none', bgcolor: '#1A3661', color: '#fff', width: { xs: '100%', sm: 'auto' }, '&:hover': { bgcolor: '#142b4f' }, boxShadow: filtersDirty && dataFotos.length > 0 ? '0 0 0 3px rgba(237, 154, 32, 0.35)' : undefined }} variant="contained" onClick={handleBuscarFotos} disabled={isLoading || informedCodesCount === 0}>
              {isLoading ? 'Buscando...' : filtersDirty && dataFotos.length > 0 ? 'Aplicar filtros' : 'Buscar Fotos'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {dataFotos.length > 0 ? <Box sx={{ width: { xs: '100%', md: 'calc(100% - 32px)', xl: 'calc(100% - 64px)' }, maxWidth: 1440, mx: 'auto', mt: 3, p: { xs: 2, md: 3 }, border: '1px solid #e3e8ef', borderRadius: 2, bgcolor: '#fff', boxShadow: '0 3px 12px rgba(26, 54, 97, 0.12)' }}>
        <div className="buttonsDiv">

          {filteredDataFotos.length > 0 && (
            <>
              <Button
                style={{ textTransform: 'none', marginLeft: 12 }}
                variant="outlined"
                onClick={handleSelectAll}
                disabled={!filteredDataFotos.length}
              >
                {filteredDataFotos.every(item => selectedProducts.includes(String(item.codigo).trim().toUpperCase()))
                  ? `Desmarcar todos (${filteredDataFotos.length})`
                  : `Selecionar todos (${filteredDataFotos.length})`}
              </Button>

              <Button
                style={{ textTransform: 'none', marginLeft: 12 }}
                variant="outlined"
                onClick={handleClearSelection}
                disabled={!selectedProducts.length}
              >
                Limpar seleção
              </Button>

              <Button
                style={{ textTransform: 'none', marginLeft: 12 }}
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleOpenDownloadOptions}
                disabled={!selectedProducts.length || isDownloadingFotos || filtersDirty}
              >
                {isDownloadingFotos
                  ? "Baixando..."
                  : `Baixar selecionados (${selectedProducts.length})`}
              </Button>
            </>
          )}
        </div>

        {dataFotos.length > 0 && filteredDataFotos.length === 0 && (
          <Alert severity="info" sx={{ mt: 3 }}>
            Nenhum dos produtos consultados possui mídia nos filtros selecionados.
          </Alert>
        )}

        <div
          ref={resultsGridRef}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${cardMinWidth}px), 300px))`,
            columnGap: 28,
            rowGap: 28,
            justifyContent: 'center',
            marginTop: 20,
            scrollMarginTop: 190
          }}
        >
          {currentPageItems.map((item, index) => (
            <ProdutoCard key={`${item.codigo}-${(currentPage - 1) * ITEMS_PER_PAGE + index}`} item={item} />
          ))}
        </div>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 3, mb: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
            />
            <Typography variant="caption" color="text.secondary">
              Exibindo {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredDataFotos.length)} de {filteredDataFotos.length}
            </Typography>
          </Box>
        )}
      </Box> : (
        <Box sx={{ mt: 3, py: { xs: 5, md: 7 }, px: 2, textAlign: 'center', border: '1px dashed #c9d3e1', borderRadius: 2, color: 'text.secondary', bgcolor: '#fafbfd' }}>
          <Typography fontWeight={600} color="#1A3661">Consulte as mídias dos produtos</Typography>
          <Typography variant="body2" sx={{ mt: 0.75 }}>Informe até 150 códigos, escolha os tipos de mídia e pressione Enter ou clique em “Buscar Fotos”.</Typography>
        </Box>
      )}

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div>
            <Typography fontWeight={600}>
              {selectedProduct?.nome || 'Produto'}
            </Typography>
            <Typography fontSize={12} color="text.secondary">
              {selectedProduct?.codigo}
            </Typography>
          </div>

          <IconButton onClick={() => setOpenModal(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => {
              handleMouseLeave();
              handleMouseLeaveVideo();
            }}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            disableRestoreFocus
            sx={previewVideo ? { pointerEvents: 'none' } : undefined}
          >
            {previewImage && (
              <img
                src={previewImage}
                alt="Pré-visualização da mídia selecionada"
                style={{
                  width: 420,
                  height: 420,
                  maxWidth: '70vw',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  background: '#fff',
                  display: 'block',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                  borderRadius: 8,
                }}
              />
            )}

            {previewVideo && (
              <video
                src={previewVideo}
                autoPlay
                muted
                loop
                disablePictureInPicture
                style={{
                  width: 400,
                  maxWidth: '70vw',
                  maxHeight: '70vh',
                  display: 'block',
                  background: '#000',
                  borderRadius: 8,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                }}
              />
            )}
          </Popover>

          {visibleModalMediaTypes.map((mediaType, categoryIndex) => {
            const option = MEDIA_OPTIONS.find(item => item.value === mediaType)!;
            const assets = mediaType === 'videos'
              ? videosDoProduto
              : fotosDoProduto.filter(media => media.source === mediaType);

            return (
              <Accordion
                key={mediaType}
                defaultExpanded={visibleModalMediaTypes.length <= 2 || categoryIndex === 0}
                sx={{ mt: categoryIndex === 0 ? 0 : 1.5 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    flexDirection: 'row-reverse',
                    bgcolor: '#f5f8fc',
                    '& .MuiAccordionSummary-content': { ml: 1 }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1A3661' }}>
                      {getMediaIcon(mediaType, 20)}
                      <Typography fontWeight={600}>{option.label} ({assets.length})</Typography>
                    </Box>
                    <Tooltip title={`Baixar somente ${option.label.toLowerCase()}`} arrow>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!assets.length}
                          aria-label={`Baixar somente ${option.label.toLowerCase()}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDownloadCategory(mediaType);
                          }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  {isLoadingModal ? (
                    <Typography fontSize={12}>Carregando mídias...</Typography>
                  ) : assets.length === 0 ? (
                    <Typography fontSize={12} color="text.secondary">
                      Nenhum arquivo encontrado nesta categoria.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 1.5 }}>
                      {assets.map(asset => (
                        <Box key={asset.id} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 1, position: 'relative' }}>
                          {mediaType === 'videos' ? (
                            <>
                              <Box
                                onMouseEnter={(event) => handleMouseEnterVideo(event, asset.previewUrl)}
                                onMouseLeave={handleMouseLeaveVideo}
                                onClick={() => handleDownloadSingleMedia(asset)}
                                role="button"
                                tabIndex={0}
                                aria-label={`Baixar ${asset.fileName}`}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    handleDownloadSingleMedia(asset);
                                  }
                                }}
                                sx={{
                                  height: 140,
                                  overflow: 'hidden',
                                  position: 'relative',
                                  borderRadius: '6px',
                                  bgcolor: '#111827',
                                  cursor: 'pointer',
                                  '&:focus-visible': { outline: '2px solid #1976d2', outlineOffset: 2 },
                                  '&:hover video': { transform: 'scale(1.035)' },
                                  '&:hover .video-hover-hint': { opacity: 0 }
                                }}
                              >
                                <Box
                                  component="video"
                                  src={asset.previewUrl}
                                  preload="metadata"
                                  muted
                                  disablePictureInPicture
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    display: 'block',
                                    transition: 'transform 220ms ease-out'
                                  }}
                                />
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    left: 8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 0.75,
                                    py: 0.35,
                                    borderRadius: 1,
                                    color: '#fff',
                                    bgcolor: 'rgba(26,54,97,0.88)',
                                    pointerEvents: 'none'
                                  }}
                                >
                                  <VideocamIcon sx={{ fontSize: 14 }} />
                                  <Typography component="span" fontSize={10} fontWeight={700} lineHeight={1}>
                                    VÍDEO
                                  </Typography>
                                </Box>
                                <Box
                                  className="video-hover-hint"
                                  sx={{
                                    position: 'absolute',
                                    left: '50%',
                                    bottom: 10,
                                    transform: 'translateX(-50%)',
                                    width: 'max-content',
                                    maxWidth: 'calc(100% - 16px)',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    color: '#fff',
                                    bgcolor: 'rgba(0,0,0,0.68)',
                                    pointerEvents: 'none',
                                    transition: 'opacity 120ms ease-out'
                                  }}
                                >
                                  <Typography fontSize={10} fontWeight={500} textAlign="center" lineHeight={1.2}>
                                    Passe o mouse para reproduzir
                                  </Typography>
                                </Box>
                              </Box>
                            </>
                          ) : (
                            <>
                              <Box
                                onClick={(event) => handleOpenImagePreview(event, asset.previewUrl)}
                                role="button"
                                tabIndex={0}
                                aria-label={`Ampliar ${asset.fileName}`}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    handleOpenImagePreview(event, asset.previewUrl);
                                  }
                                }}
                                sx={{
                                  height: 140,
                                  overflow: 'hidden',
                                  borderRadius: '6px',
                                  cursor: 'zoom-in',
                                  bgcolor: '#fff',
                                  '&:hover img': { transform: 'scale(1.06)' },
                                  '&:focus-visible': { outline: '2px solid #1976d2', outlineOffset: 2 }
                                }}
                              >
                                <Box
                                  component="img"
                                  src={asset.previewUrl}
                                  alt={asset.fileName}
                                  loading="lazy"
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    display: 'block',
                                    transition: 'transform 220ms ease-out'
                                  }}
                                />
                              </Box>
                              <Tooltip title={`Baixar ${asset.fileName}`} arrow>
                                <IconButton
                                  size="small"
                                  aria-label={`Baixar ${asset.fileName}`}
                                  onClick={() => handleDownloadSingleMedia(asset)}
                                  sx={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    bgcolor: 'rgba(255,255,255,0.92)',
                                    boxShadow: '0 1px 5px rgba(0,0,0,0.18)',
                                    '&:hover': { bgcolor: '#fff' }
                                  }}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          <Typography fontSize={11} mt={1} noWrap title={asset.fileName}>
                            {asset.fileName.replace(/\.[^.]+$/, '')}
                          </Typography>
                          {mediaType === 'videos' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.5, color: 'text.secondary' }}>
                              <DownloadIcon sx={{ fontSize: 13 }} />
                              <Typography fontSize={10} lineHeight={1.2}>
                                Clique no vídeo para baixar
                              </Typography>
                            </Box>
                          )}
                          {asset.system && (
                            <Typography fontSize={10} color="text.secondary" noWrap title={asset.system}>
                              Sistema: {asset.system}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 20
            }}
          >
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadAll}
              disabled={(fotosDoProduto.length === 0 && videosDoProduto.length === 0) || isDownloadingFotos || isDownloadingVideos}
            >
              {isDownloadingFotos || isDownloadingVideos ? "Baixando..." : "Baixar tipos filtrados"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isLoading}
        disableEscapeKeyDown
        aria-labelledby="media-search-progress-title"
        aria-describedby="media-search-progress-description"
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(7, 22, 43, 0.62)',
              backdropFilter: 'blur(2px)'
            }
          }
        }}
        sx={{
          '& .MuiDialog-paper': {
            position: 'relative',
            width: 'calc(100% - 40px)',
            maxWidth: 480,
            m: 0,
            borderRadius: 4,
            overflow: 'hidden',
            border: '1px solid rgba(26, 54, 97, 0.08)',
            boxShadow: '0 24px 64px rgba(7, 22, 43, 0.26)',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: '0 0 auto 0',
              height: 5,
              background: 'linear-gradient(90deg, #102a4c 0%, #1976d2 55%, #57a6e6 100%)'
            }
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', px: { xs: 3, sm: 5 }, py: { xs: 4, sm: 5 }, overflowX: 'hidden' }}>
          <Box sx={{
            width: 64,
            height: 64,
            borderRadius: '20px',
            display: 'grid',
            placeItems: 'center',
            color: '#1976d2',
            bgcolor: 'rgba(25, 118, 210, 0.09)',
            mx: 'auto',
            mb: 2.5
          }}>
            <SearchRoundedIcon sx={{ fontSize: 34 }} />
          </Box>

          <Typography
            id="media-search-progress-title"
            variant="h4"
            sx={{ color: '#102a4c', fontWeight: 700, fontSize: { xs: '1.55rem', sm: '1.8rem' } }}
          >
            Buscando Imagens
          </Typography>
          <Typography
            id="media-search-progress-description"
            sx={{ mt: 1, color: 'text.secondary', fontSize: '0.98rem' }}
          >
            Estamos localizando e preparando as mídias dos produtos para você.
          </Typography>

          <Box sx={{ width: '100%', mt: 3.5 }}>
            <LinearProgress
              variant={searchProgress.total > 0 && searchProgress.completed > 0 ? 'determinate' : 'indeterminate'}
              value={searchProgress.total > 0 && searchProgress.completed > 0
                ? Math.min(100, Math.round((searchProgress.completed / searchProgress.total) * 100))
                : undefined}
              aria-label="Progresso da busca de imagens"
              sx={{
                height: 8,
                borderRadius: 999,
                bgcolor: 'rgba(25, 118, 210, 0.12)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  background: 'linear-gradient(90deg, #15345f, #2494e8)',
                  transition: 'transform 450ms ease-out'
                }
              }}
            />
            <Typography sx={{ mt: 1.25, color: 'text.secondary', fontSize: '0.78rem', letterSpacing: '0.02em' }}>
              {searchProgress.total > 0
                ? `${Math.min(searchProgress.completed, searchProgress.total)} de ${searchProgress.total} produtos consultados`
                : 'Preparando a consulta das mídias'}
            </Typography>
          </Box>

          <Box sx={{
            width: '100%',
            boxSizing: 'border-box',
            mt: 2.5,
            py: 1.5,
            px: 2,
            borderRadius: 2.5,
            bgcolor: '#f5f8fc',
            border: '1px solid rgba(26, 54, 97, 0.08)'
          }}>
            <Typography sx={{ color: '#1a3661', fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.5 }}>
              {searchProgress.completed === 0
                ? 'Iniciando busca nos produtos...'
                : searchProgress.completed < searchProgress.total
                  ? `Consultando SKU ${searchProgress.currentSku}`
                  : 'Finalizando as informações dos produtos...'}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.82rem', lineHeight: 1.5 }}>
              {searchProgress.mediaFound} {searchProgress.mediaFound === 1 ? 'mídia localizada' : 'mídias localizadas'} até agora
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isDownloadingFotos || isDownloadingVideos}
        disableEscapeKeyDown
        aria-labelledby="download-progress-title"
        sx={{ '& .MuiDialog-paper': { width: '85%', maxWidth: 450, borderRadius: 3 } }}
      >
        <DialogTitle
          id="download-progress-title"
          sx={{ textAlign: 'center', fontSize: { xs: '1.65rem', sm: '2rem' }, pt: 4, pb: 1.5 }}
        >
          Preparando Download
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', px: { xs: 3, sm: 6 }, pb: 4 }}>
          <BouncingDots />
          <Typography variant="h6" sx={{ mt: 2.5, mb: 2.5 }}>
            Estamos preparando seus arquivos, só um instante...
          </Typography>
          <Typography sx={{ mb: 2 }} color="text.secondary">
            {downloadProgress.total > 0
              ? `${downloadProgress.completed} de ${downloadProgress.total} arquivos processados`
              : 'Calculando os arquivos para download...'}
          </Typography>
          <LinearProgress
            variant={downloadProgress.total > 0 ? 'determinate' : 'indeterminate'}
            value={downloadProgress.total > 0
              ? Math.round((downloadProgress.completed / downloadProgress.total) * 100)
              : undefined}
            sx={{ height: 10, borderRadius: 5 }}
          />
          {downloadProgress.currentMedia && (
            <Typography color="text.secondary" fontSize={12} sx={{ mt: 1.5 }} noWrap title={downloadProgress.currentMedia}>
              Baixando: {downloadProgress.currentMedia}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(false)}>
        <Alert onClose={() => setOpenSnackbar(false)} severity={typeMessage} sx={{ width: '100%' }}>{snackbarMessage}</Alert>
      </Snackbar>

      <Dialog
        open={openDownloadModal}
        onClose={() => setOpenDownloadModal(false)}
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiDialog-paper': { maxWidth: 560, borderRadius: 2 } }}
      >
        <DialogTitle sx={{ backgroundColor: '#1A3661', color: '#fff' }}>
          Preparar download
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography color="text.secondary" sx={{ mb: 2.5 }}>
            Configure o pacote para os <b>{selectedProducts.length}</b> produtos selecionados.
          </Typography>

          <Box sx={{ bgcolor: '#f5f8fc', border: '1px solid #e3e8ef', borderRadius: 2, p: 2 }}>
            <Typography fontSize={13} fontWeight={600} color="#1A3661" sx={{ mb: 1.25 }}>
              Tipos incluídos pelo filtro da busca
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {activeMediaTypes.map(mediaType => {
                const option = MEDIA_OPTIONS.find(item => item.value === mediaType)!;
                return (
                  <Box
                    key={mediaType}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: '#fff', color: '#1A3661', border: '1px solid #dce4ef', borderRadius: 1.5, px: 1.25, py: 0.75 }}
                  >
                    {getMediaIcon(mediaType, 17)}
                    <Typography fontSize={12}>{option.shortLabel}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <FormControl component="fieldset" fullWidth sx={{ mt: 3 }}>
            <FormLabel component="legend" sx={{ color: '#1A3661', fontWeight: 600, mb: 1 }}>
              Organização do ZIP
            </FormLabel>
            <RadioGroup
              value={zipOrganization}
              onChange={event => setZipOrganization(event.target.value as ZipOrganization)}
            >
              <FormControlLabel value="by_sku" control={<Radio size="small" />} label="Uma pasta para cada SKU" />
              <FormControlLabel value="by_type" control={<Radio size="small" />} label="Pastas separadas por tipo de mídia" />
              <FormControlLabel value="flat" control={<Radio size="small" />} label="Todos os arquivos na pasta principal" />
            </RadioGroup>
          </FormControl>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
            <Button onClick={() => setOpenDownloadModal(false)} sx={{ textTransform: 'none' }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleDownloadMultiple}
              startIcon={<DownloadIcon />}
              sx={{ textTransform: 'none', backgroundColor: '#1A3661' }}
            >
              Preparar download
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
}
